<template>
  <!--
    ボリュームディスカウントの帯の表（入力）と試算。
    **請求管理の［人数割設定］の小窓と、契約企業一覧の「料金・割引」タブ、［プランを追加］の小窓で共有する。**
    片方だけ直して、プランと会社で帯の入れ方・見え方がずれないように。
  -->
  <div class="volume-discount-tiers">
    <div class="table-scroll">
      <table class="volume-discount-tier-table">
        <thead>
          <tr>
            <th :id="headId('seat')">人数（〜名から）</th>
            <th :id="headId('rate')" class="numeric">割引率（%）</th>
            <th class="numeric">1人あたりの単価</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(tier, index) in value" :key="index" class="volume-discount-tier">
            <td>
              <!--
                **最初の帯は1名から固定。**人数の欄は変えられない（帯の間に穴を作らない。設計の決まり2）。
              -->
              <input
                :value="tier.fromSeat"
                type="number"
                min="1"
                class="form-control tier-seat"
                :aria-labelledby="headId('seat')"
                :disabled="disabled || index === 0"
                @input="set(index, 'fromSeat', $event.target.value)"
              >
            </td>
            <td class="numeric">
              <input
                :value="tier.discountRate"
                type="number"
                min="0"
                max="99"
                class="form-control tier-rate"
                :aria-labelledby="headId('rate')"
                :disabled="disabled"
                @input="set(index, 'discountRate', $event.target.value)"
              >
            </td>
            <!-- **定価から計算して見せるだけ。**保存するのは率だけ（単価は請求の計算のとき、その月の定価から出す）。 -->
            <td class="numeric tier-unit-price">{{ unitPriceText(tier) }}</td>
            <td class="tier-remove">
              <!-- 条件に「>」を書かない（ボタンの色の検査がタグを「>」までで読むため）。 -->
              <CButton
                v-if="index !== 0 && !disabled"
                type="button"
                size="sm"
                color="danger"
                variant="outline"
                @click="remove(index)"
              >削除</CButton>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!disabled" class="volume-discount-tiers__buttons">
      <!-- 帯は10本まで（back も11本以上を断る）。 -->
      <CButton
        type="button"
        size="sm"
        color="secondary"
        variant="outline"
        class="tier-add"
        :disabled="value.length >= maxTiers"
        @click="add"
      >帯を追加</CButton>
      <!--
        **帯を「1名から0%」の1行にするだけ。**保存は［保存する］で（設計の決まり14：
        ある日から割引をやめるにも、その日からの「割引なし」の記録が要る）。
      -->
      <CButton
        v-if="showClear"
        type="button"
        size="sm"
        color="secondary"
        variant="outline"
        class="tier-clear"
        @click="clear"
      >割引を設定しない</CButton>
    </div>

    <!--
      試算。**今の定価と、いま入れている帯で計算して見せる。**帯に断りがある間と、
      定価・基本料が分からないときは出さない（間違った額を見せない）。
    -->
    <ul v-if="trialLines.length" class="volume-discount-trials">
      <li v-for="line in trialLines" :key="line">{{ line }}</li>
    </ul>
  </div>
</template>

<script>
import {
  MAX_TIERS,
  amountText,
  integerOrNull,
  tierProblem,
  trialAmounts,
  unitPrice
} from '@/utils/volumeDiscount'

/** 試算に並べる人数（指示の紙のとおり）。 */
const TRIAL_SEATS = [10, 30, 50, 100]

/* 試算の文（V12）。{金額} は2つ。前が割引後、後ろが定価（どちらも基本料込み）。 */
const TRIAL_TEMPLATE = '【試算】{人数}名の場合：{金額}円（定価：{金額}円）※基本料を含む'

let instanceCount = 0

export default {
  name: 'VolumeDiscountTierEditor',

  props: {
    /** 帯の行 [{ fromSeat, discountRate }]。入力の途中は文字列のまま持つ（v-model）。 */
    value: { type: Array, required: true },
    /** 1人あたりの定価（整数円）。分からなければ null（単価も試算も出さない）。 */
    pricePerUser: { type: [Number, String], default: null },
    /** 基本料（整数円）。分からなければ null（試算を出さない）。 */
    basePrice: { type: [Number, String], default: null },
    /** 見るだけ・送っているあいだ。 */
    disabled: { type: Boolean, default: false },
    /** ［割引を設定しない］を出すか。タブには「適用しない」の選択肢があるので出さない。 */
    showClear: { type: Boolean, default: true }
  },

  data () {
    instanceCount += 1
    return { instance: instanceCount, maxTiers: MAX_TIERS }
  },

  computed: {
    listPrice () {
      return integerOrNull(this.pricePerUser)
    },

    trialLines () {
      const price = this.listPrice
      const base = integerOrNull(this.basePrice)
      if (price === null || base === null) return []
      if (tierProblem(this.value)) return []
      return TRIAL_SEATS.map(seats => {
        const trial = trialAmounts(seats, this.value, price, base)
        return TRIAL_TEMPLATE
          .replace('{人数}', String(trial.seats))
          .replace('{金額}', amountText(trial.amount))
          .replace('{金額}', amountText(trial.listAmount))
      })
    }
  },

  methods: {
    headId (name) {
      return 'volume-discount-' + this.instance + '-' + name
    },

    unitPriceText (tier) {
      const rate = integerOrNull(tier.discountRate)
      if (this.listPrice === null || rate === null || rate < 0 || rate > 99) return '—'
      return amountText(unitPrice(this.listPrice, rate)) + '円'
    },

    /** 行を写してから変える（親の配列を直に書き換えない）。 */
    rows () {
      return this.value.map(tier => ({ fromSeat: tier.fromSeat, discountRate: tier.discountRate }))
    },

    set (index, key, text) {
      const rows = this.rows()
      rows[index][key] = text
      this.$emit('input', rows)
    },

    add () {
      if (this.disabled || this.value.length >= MAX_TIERS) return
      this.$emit('input', this.rows().concat([{ fromSeat: '', discountRate: '' }]))
    },

    remove (index) {
      // 最初の帯（1名から）は消させない。
      if (this.disabled || index <= 0) return
      const rows = this.rows()
      rows.splice(index, 1)
      this.$emit('input', rows)
    },

    clear () {
      if (this.disabled) return
      this.$emit('input', [{ fromSeat: '1', discountRate: '0' }])
    }
  }
}
</script>

<style scoped>
.volume-discount-tiers { margin-top: 0.75rem; }

.table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.volume-discount-tier-table {
  width: 100%;
  border-collapse: collapse;
}

.volume-discount-tier-table th,
.volume-discount-tier-table td {
  border-bottom: 1px solid #d8dbe0;
  padding: 0.4rem 0.5rem;
  vertical-align: middle;
}

.volume-discount-tier-table th {
  font-size: 0.8rem;
  color: #768192;
  font-weight: 500;
  white-space: nowrap;
}

.numeric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.tier-seat,
.tier-rate {
  max-width: 7rem;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.tier-rate { margin-left: auto; }

.tier-unit-price { white-space: nowrap; }

.tier-remove { width: 1%; white-space: nowrap; }

.volume-discount-tiers__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.6rem;
}

.volume-discount-trials {
  margin: 0.75rem 0 0;
  padding-left: 0;
  list-style: none;
  font-size: 0.8rem;
  color: #3c4b64;
  font-variant-numeric: tabular-nums;
}

.volume-discount-trials li + li { margin-top: 0.15rem; }
</style>
